import React, { useCallback, useRef, useState } from 'react';
import type { CalendarPost } from '../types';
import { dayKey } from './calendarDates';
import { isMovablePost } from './calendarFilterModel';
import { canDropAt, isRealMove, KeyboardMoveKey, keyboardMoveTime, movedTime } from './calendarMoveTime';

const ARROWS: readonly string[] = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

/** The drag and keyboard handlers of a card. A post that cannot move gets `draggable: false` only. */
export interface CardDragHandlers {
  draggable: boolean;
  onDragStart?: (event: React.DragEvent) => void;
  onDragEnd?: () => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

interface UseDragMoveProps {
  view: 'month' | 'week';
  /** Called with a new time ahead of now. */
  onMove: (post: CalendarPost, scheduledAt: Date) => void;
  /** Called when a drop goes to a time in the past. */
  onRefused: () => void;
  /** The texts of the live region. */
  announce: {
    pickedUp: (post: CalendarPost) => string;
    moveTo: (time: Date) => string;
    canceled: () => string;
  };
}

/**
 * Drag to move (design b6), with the pointer and with the keyboard. Only a draft, a scheduled
 * post and a post that needs you move (`isMovablePost`). Month keeps the time of day; Week takes
 * the time of the hour that gets the drop. A drop on a time in the past is refused.
 *
 * Keyboard: Space picks the card up, the arrows move it, Enter drops it, Escape cancels. Each step
 * goes to the `aria-live` text.
 */
export function useDragMove({ view, onMove, onRefused, announce }: UseDragMoveProps) {
  const dragged = useRef<CalendarPost | null>(null);
  const [draggingUuid, setDraggingUuid] = useState<string | null>(null);
  const [overDayKey, setOverDayKey] = useState<string | null>(null);
  const [held, setHeld] = useState<{ post: CalendarPost; target: Date } | null>(null);
  const [liveText, setLiveText] = useState('');

  const drop = useCallback(
    (post: CalendarPost, target: Date | null) => {
      if (target !== null && !isRealMove(post.scheduledAt, target)) {
        return;
      }
      if (canDropAt(target)) {
        onMove(post, target);
      } else {
        onRefused();
      }
    },
    [onMove, onRefused]
  );

  const endDrag = useCallback(() => {
    dragged.current = null;
    setDraggingUuid(null);
    setOverDayKey(null);
  }, []);

  const cardHandlers = useCallback(
    (post: CalendarPost): CardDragHandlers => {
      if (!isMovablePost(post)) {
        return { draggable: false };
      }
      return {
        draggable: true,
        onDragStart: (event: React.DragEvent) => {
          dragged.current = post;
          setDraggingUuid(post.uuid);
          // jsdom and some test events have no data transfer.
          const transfer = event.dataTransfer as DataTransfer | null;
          if (transfer !== null) {
            transfer.setData('text/plain', post.uuid);
            transfer.effectAllowed = 'move';
          }
        },
        onDragEnd: endDrag,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (held === null || held.post.uuid !== post.uuid) {
            if (event.key === ' ') {
              event.preventDefault();
              setHeld({ post, target: new Date(post.scheduledAt) });
              setLiveText(announce.pickedUp(post));
            }
            return;
          }
          if (ARROWS.includes(event.key)) {
            event.preventDefault();
            const target = keyboardMoveTime(held.target, event.key as KeyboardMoveKey, view, event.shiftKey);
            setHeld({ post, target });
            setLiveText(announce.moveTo(target));
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setHeld(null);
            drop(post, held.target);
          } else if (event.key === 'Escape') {
            event.preventDefault();
            setHeld(null);
            setLiveText(announce.canceled());
          }
        },
      };
    },
    [announce, drop, endDrag, held, view]
  );

  /** The handlers of a day (Month) or of an hour of a day (Week: `minutes` after midnight). */
  const dayHandlers = useCallback(
    (targetDayKey: string, minutes?: number) => ({
      onDragOver: (event: React.DragEvent) => {
        if (dragged.current === null) {
          return;
        }
        event.preventDefault();
        setOverDayKey(targetDayKey);
      },
      onDrop: (event: React.DragEvent) => {
        const post = dragged.current;
        if (post === null) {
          return;
        }
        event.preventDefault();
        endDrag();
        drop(post, movedTime(post.scheduledAt, targetDayKey, minutes));
      },
    }),
    [drop, endDrag]
  );

  const isHolding = draggingUuid !== null || held !== null;
  const targetDayKey = held === null ? overDayKey : dayKey(held.target);

  return {
    isHolding,
    /** The post that the pointer or the keyboard holds. */
    heldUuid: held?.post.uuid ?? draggingUuid,
    /** The day that gets the drop now. */
    targetDayKey,
    /** The time of a post that the keyboard holds. */
    keyboardTarget: held?.target ?? null,
    liveText,
    cardHandlers,
    dayHandlers,
  };
}

export type DragMove = ReturnType<typeof useDragMove>;
